/* PROCESS:
   * Enter all the required machine and cone label data
   * Save record as "initialized" to the database
   * Return to calling scan production page
 OR
   * Look up the cone # to retrieve the production record based on the value entered
   * If found, allow the user to complete the previously initiated record
      * Select if the item scanned is "GOOD" or "BAD"
         * If BAD, select a defect type.
      * Enter the package weight
      * Complete or cancel updating of the record
*/
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { Col, Input, Row } from "reactstrap";
import { FilterSelect, ServiceThreadSpinner } from ".";
import { ItemTypeahead, SpinnerButton } from './';
import { DesktopContainer } from "../layouts";
import { api, constants, filterHelpers, UserContext } from "../utils";
import _ from "lodash";

export default function NewProductionRecord(props) {
   const navigate = useNavigate();
   const sleep = ms => new Promise(r => setTimeout(r, ms));
   const { currentUser } = useContext(UserContext);
   const SELECT_OPTION = { value: 0, label: "-- Select --" };
   const ALL_OPTION = { value: 0, label: "N/A" };
   const NA_OPTION = { value: "N/A", label: "N/A" };
   const ALL_MACHINES = {
      value: 0,
      label: "-- Select --",
      activeMachines: "0",
      spindles: 0,
      sides: false,
   };
   const INIT_MODEL = {
      id: 0,
      coneId: 0,
      defectId: 0, // not defined yet
      isExceptionMachine: true,
      isGood: false,
      item: "",
      machineId: 0,
      machineNum: 0,
      maxOverage: 0,
      maxWeight: 0,
      minWeight: 0,
      pkgWeight: 0,
      side: "N/A",
      spindle: 0
   };
   const [filters, setFilters] = useState([
      { filterName: "machineId", value: ALL_MACHINES },
      { filterName: "machineNum", value: ALL_OPTION },
      { filterName: "spindle", value: ALL_OPTION },
      { filterName: "side", value: NA_OPTION },
      { filterName: "defectId", value: SELECT_OPTION },
      { filterName: 'coneId', value: 0 },
      { filterName: 'isGood', value: false },
      { filterName: 'item', value: null },
      { filterName: 'weight', value: 0 }
   ]);
   const cone = filterHelpers.getValue(filters, "coneId");
   const isGood = filterHelpers.getValue(filters, "isGood");
   const item = filterHelpers.getValue(filters, "item");
   const weight = filterHelpers.getValue(filters, "weight");
   const [defects, setDefects] = useState([]);         // Defect types for dropdown
   const [disabled, setDisabled] = useState(false);    // Disable good checkbox and defect list when appropriate
   const [editId, setEditId] = useState(0);            // Cone Id entered to find and complete record with
   const [initialized, setInit] = useState(false);     // Has screen been initialized with drop-downs?
   const [loaded, setLoaded] = useState(false);        // Editing existing record?
   const [machines, setMachines] = useState([]);       // Active machine types 
   const [machineNums, setMachineNums] = useState([]); // Active machines for selected machine type
   const [model, setModel] = useState(INIT_MODEL);     // Production Record to be saved
   const [saving, setSave] = useState(false);          // Show or hide spinner on spinner buttons
   const [sides, setSides] = useState([]);             // Sides available, if any, for selected machine
   const [spin, setSpinner] = useState(true);          // Shows or hides spinner component
   const [spindles, setSpindles] = useState([]);       // Spindles available for selected machine

   useEffect(() => {
      if (!currentUser) return;
      api
         .fetch(`Production/InitConeLists`)
         .then((response) => {
            if (response.data.success) {
               const machineList = _.concat(
                  [ALL_MACHINES],
                  _.map(response.data.machines, (m) => ({
                     value: m.machineId,
                     label: m.machineName,
                     activeMachines: m.activeMachines,
                     spindles: m.spindles,
                     sides: m.hasBSide,
                  }))
               );
               const defectList = _.concat(
                  [SELECT_OPTION],
                  _.map(response.data.defects, (d) => ({
                     value: d.id,
                     label: d.defect,
                     isDisabled: d.id === 9 // Under MSR Weight is auto-selected, operator cannot select
                  }))
               );
               return {
                  machineList: machineList,
                  defectList: defectList,
               };
            } else {
               throw new Error(response);
            }
         })
         .then(({ machineList, defectList }) => {
            setMachines(machineList);
            setDefects(defectList);
            // These should only fill in after a machine is selected, just put in a default selections
            setMachineNums([ALL_OPTION]);
            setSides([NA_OPTION]);
            setSpindles([ALL_OPTION]);
            setInit(true);
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setSpinner(false);
            inputFocus("coneFetch");
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentUser]);

   useEffect(() => {
      // Either the various lists are not populated or the model was not retrieved/loaded
      if (!initialized || !loaded)
         return;

      async function setLists() {
         // set child drop-down options as appropriate for the machine tied to the record being edited
         const mach = _.find(machines, (x) => x.value === model.machineId);
         let newMachList = await fixMachines(mach.activeMachines);
         let newSpindleList = await fixSpindles(mach.spindles);
         let newSideList = await fixSides(mach.sides);

         // Set ddl selected values based on the record being edited
         const machFilter = model.machineId > 0
            ? _.find(machines, (m) => m.value === model.machineId)
            : ALL_MACHINES;
         let newFilters = filterHelpers.getFilters(filters, {
            filterName: "machineId",
            value: machFilter,
         });

         const numFilter = model.machineNum > 0
            ? _.find(newMachList, (m) => m.value === model.machineNum)
            : ALL_OPTION;
         newFilters = filterHelpers.getFilters(newFilters, {
            filterName: "machineNum",
            value: numFilter,
         });

         const spindleFilter = model.spindle > 0
            ? _.find(newSpindleList, (s) => s.value === model.spindle)
            : ALL_OPTION;
         newFilters = filterHelpers.getFilters(newFilters, {
            filterName: "spindle",
            value: spindleFilter,
         });

         const sideFilter = model.side !== ""
            ? _.find(newSideList, (s) => s.value === model.side)
            : NA_OPTION;
         newFilters = filterHelpers.getFilters(newFilters, {
            filterName: "side",
            value: sideFilter,
         });

         //--------------------------------------------------------------
         // Items below should not be set yet, but check for safety sake
         //--------------------------------------------------------------
         const defectFilter = model.defectId > 0
            ? _.find(defects, (d) => d.value === model.defectId)
            : SELECT_OPTION;
         newFilters = filterHelpers.getFilters(newFilters, {
            filterName: "defectId",
            value: defectFilter,
         });

         // Set filters not related to drop-down lists
         newFilters = filterHelpers.getFilters(newFilters, {
            filterName: "coneId",
            value: model.coneId,
         });

         newFilters = filterHelpers.getFilters(newFilters, {
            filterName: "isGood",
            value: model.isGood,
         });

         newFilters = filterHelpers.getFilters(newFilters, {
            filterName: "weight",
            value: model.pkgWeight,
         });

         setFilters(newFilters);
      }

      setLists();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [initialized, loaded]);

   useEffect(() => {
      if (model && model.isExceptionMachine === false) { // Not an exception machine
         // Over MSR Weight defect is auto-selected only, operator cannot select
         setDefects(defects.map(item => item.value === 5 ? { ...item, isDisabled: true} : item))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [model]);

   function cancelRecord() {      
      var nav = constants.ENDPOINTS.scanProduction;
      navigate(nav); // Return to calling scanner page
   }

   const fixMachines = async (activeMachines) => {
      // Fill in the drop-down with active machines for the selected machine
      var nums = activeMachines.split(", ");
      let newMachineList = _.concat(
         [ALL_OPTION],
         _.map(nums, (m) => ({ value: parseInt(m, 10), label: m }))
      );
      setMachineNums(newMachineList);
      return newMachineList;
   };

   const fixSides = async (hasBSide) => {
      // Fill in the drop-down with available sides for the selected machine
      let newSideList = [NA_OPTION];
      if (hasBSide === true) {
         newSideList = _.concat(newSideList, _.map([{ value: "A", label: "A" }]));
         newSideList = _.concat(newSideList, _.map([{ value: "B", label: "B" }]));
      }
      setSides(newSideList);
      return newSideList;
   };

   const fixSpindles = async (spindlesPerSide) => {
      // Fill in the drop-down with available spindles for the selected machine
      let newSpindleList = [ALL_OPTION];
      for (let i = 1; i <= spindlesPerSide; i++) {
         newSpindleList = _.concat(
            newSpindleList,
            _.map([{ value: i, label: i.toString() }])
         );
      }
      setSpindles(newSpindleList);
      return newSpindleList;
   };

   const handleFocus = (event) => event.target.select();

   async function inputFocus(fieldname) {
      await sleep(50);  // allows time for input to be recognized
      const el = document.querySelector(`input[name=${fieldname}]`);
      if (el !== null) {
         await el.focus();
      }
   }

   function onConeChange(val) {
      if (val === undefined || val === null || val === "") val = 0;
      onFilterChange({ filterName: "coneId", value: val, filteredDisplay: '' })
      let changes = Object.assign({}, model);
      changes.coneId = val;
      setModel(changes);
      inputFocus("pkgWgt");
   }

   function onDefectChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      if (newFilterValue.value < 1)
         changes.defectId = 1;
      else
         changes.defectId = newFilterValue.value;
      setModel(changes);
      inputFocus("pkgWgt");
   }

   function onFilterChange(changedFilter) {
      const filterChanges = filterHelpers.getFilters(filters, changedFilter);
      setFilters(filterChanges);
   }

   function onGoodVsBad(opt) {
      let changes = Object.assign({}, model);
      changes.isGood = opt;
      setModel(changes);
      onFilterChange({ filterName: "isGood", value: opt, filteredDisplay: '' })
   }

   function onItemChange(opt) {
      onFilterChange({ filterName: "item", value: opt, filteredDisplay: '' })
      let changes = Object.assign({}, model);
      if (opt === undefined || opt === null)
         changes.item = "";
      else
         changes.item = opt.itemNumber;
      setModel(changes);
   }

   const onMachineChange = async (newFilterValue) => {
      // Fill in the machine related drop-down lists with proper values
      const mach = _.find(machines, (x) => x.value === newFilterValue.value);
      let newMachList = await fixMachines(mach.activeMachines);
      let newSpindleList = await fixSpindles(mach.spindles);
      let newSideList = await fixSides(mach.sides);

      // Set the machine dependent filters to their defaults
      const numFilter = _.find(newMachList, (m) => m.value === 0);
      const spindleFilter = _.find(newSpindleList, (s) => s.value === 0);
      const sideFilter = _.find(newSideList, (s) => s.value === "N/A");

      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "machineId",
         value: mach,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "machineNum",
         value: numFilter,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "spindle",
         value: spindleFilter,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "side",
         value: sideFilter,
      });
      setFilters(newFilters);

      // Set the data values
      let changes = Object.assign({}, model);
      changes.machineId = newFilterValue.value;
      changes.machineNum = ALL_OPTION.value;
      changes.side = NA_OPTION.value;
      changes.spindle = ALL_OPTION.value;
      setModel(changes);
   };

   function onMachineNumChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      changes.machineNum = newFilterValue.value;
      setModel(changes);
   }

   function onSideChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      changes.side = newFilterValue.value;
      setModel(changes);
   }

   function onSpindleChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      changes.spindle = newFilterValue.value;
      setModel(changes);
      inputFocus("coneNum");
   }

   function onWeightChange(val) {
      if (val === undefined || val === null || val === "") val = 0;

      let changes = Object.assign({}, model);
      if (val < changes.minWeight) {
         // operator may still be updating, but we need to enforce this until then
         resetWeight(9, false, val, true);
         changes.isGood = false;
         changes.defectId = 9;
         changes.defect = "Under MSR Weight";
      }
      else if (val > changes.maxWeight) {
         // need to enforce the overweight issue
         resetWeight(5, false, val, true);
         changes.isGood = false;
         changes.defectId = 5;
         changes.defect = "Over MSR Weight";
      }
      else {
         // operator may still be updating, but for now it is within spec
         resetWeight(0, true, val, false);
         changes.isGood = true;
         changes.defectId = 1;
         changes.defect = "Good";
      }
      changes.pkgWeight = val;
      setModel(changes);
      inputFocus("pkgWgt");
   }

   function loadRecord() {
      if (!editId || editId === 0) return;

      if (editId < 1) {
         toast.error("Error: not a valid cone number");
         return;
      }

      // The user has entered a cone# to search for rather than make a new entry
      // We need to see if the record exists and can be completed at this time
      setSpinner(true);
      let lookup = false;
      api
         .fetch(`Production/GetProductionRecordByConeId/${editId}/${lookup}`)
         .then(r => {
            if (r.data.success) {
               if (r.data.message !== null && r.data.message !== undefined) {
                  setModel(r.data.message);
                  setLoaded(true);
                  inputFocus("pkgWgt");
               } else {
                  toast.error("No data returned!");
               }
            } else {
               toast.error(r.data.message);
               inputFocus("coneFetch");

            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            inputFocus("coneFetch");
         })
         .finally(() => {
            setEditId(0);
            setSpinner(false);
         });
   }

   function resetWeight(dfctId, goodVal, wgtVal, isDisabled) {
      const dfct = _.find(defects, (x) => x.value === dfctId);
      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "defectId",
         value: dfct,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "isGood",
         value: goodVal,
         filteredDisplay: ''
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "weight",
         value: wgtVal,
      });
      setFilters(newFilters);
      setDisabled(isDisabled);
   }

   function saveRecord() {
      let warnings = [];
      if (model?.item === "") {
         warnings.push("Item # is required\n");
      }
      if (model?.machineId === 0) {
         warnings.push("Machine is required\n");
      }
      if (model?.machineNum === 0) {
         warnings.push("Machine # is required\n");
      }
      if (model?.side === "N/A" && sides.length > 1) {
         warnings.push("Side is required\n");
      }
      if (model?.spindle === 0 && spindles.length > 1) {
         warnings.push('Spindle is required\n');
      }
      if (model?.coneId === 0) {
         warnings.push("Cone # is required\n");
      }
      if (warnings.length) {
         toast.error(warnings.join(''));
         return;
      }

      setSave(true);
      api
         .post(`Production/SaveConeRecord`, model)
         .then(response => {
            if (response.data.success) {
               toast.success("Production recorded successfully");
               cancelRecord();
            }
            else {
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setSave(false);
         });
   }

   function updateRecord() {
      if (model.id === 0) {
         toast.error("Record identifier missing!");
         return;
      }

      let failures = [];
      if (model.pkgWeight <= 0) {
         failures.push("Package weight is required\n");
      }
      else if(model.pkgWeight > model.maxOverage) {
         failures.push("The max weight limit has been exceeded! Please check your package weight.\n");
      }
      if (isGood === false && model.defectId === 1) {
         failures.push("Defect is required\n");
      }
      if (failures.length) {
         toast.error(failures.join(''));
         return;
      }

      setSave(true);
      api
         .post(`Production/CompleteConeRecord`, model)
         .then(response => {
            if (response.data.success) {
               toast.success("Production recorded complete!");
               cancelRecord();
            }
            else {
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setSave(false);
         });
   }

   return (
      <DesktopContainer screenName="New Production Record">
         <ServiceThreadSpinner on={spin}>
            <div className="filter-header">
               {/* Header options */}
               {!loaded ? (
                  <div>
                     <Row className="align-items-center my-1 mx-2">
                        <Col className="h6 fw-bold my-0" xs={12}>Enter Cone# to Complete:</Col>
                     </Row>
                     <Row className="align-items-center my-1 mx-2">
                        <Col xs={12}>
                           <Input className="no-spinner h-100"  // This className hides the up/down arrows at end of entry field
                              type="number"
                              name="coneFetch"
                              value={editId}
                              onFocus={handleFocus}
                              onChange={(e) => {
                                 if (e.target.value.match(/^\d{1,9}$/)) // integer from 1 to 9 digits
                                    setEditId(e.target.value);
                              }}
                           />
                        </Col>
                     </Row>
                     <Row className="lineheight8">&nbsp;</Row>
                     <Row className="align-items-center my-1 mx-2">
                        <Col xs={12}>
                           <SpinnerButton
                              title="Load"
                              className="shredButton btn-md w-100"
                              onClick={() => loadRecord()}
                              on={saving}
                              disabled={saving || loaded}
                           > Load
                           </SpinnerButton>
                        </Col>
                     </Row>
                  </div>
               ) : null}

               <br />
               <Row className="align-items-center my-1 mx-2">
                  <Col className="h6 fw-bold my-0" xs={12}>Enter Requirements Below:</Col>
               </Row>
               {/* Identify item being recorded */}
               {model?.id === 0 ? (
                  <div>
                     <Row className="align-items-center my-1 mx-2">
                        <Col className="h6 fw-bold my-0" xs={12}>Item:</Col>
                     </Row>
                     <Row className="align-items-center my-1 mx-2">
                        <Col xs={12}>
                           <ItemTypeahead
                              name="itemInput"
                              value={item}
                              onChange={onItemChange}
                              disabled={model.id !== 0}
                           />
                        </Col>
                     </Row>
                  </div>
               ) : (
                  <Row className="align-items-center my-1 mx-2">
                     <Col className="h6 fw-bold my-0" xs={12}>{model.itemWithDesc}</Col>
                  </Row>
               )}

               {/* Identify machine type being recorded */}
               <Row className="lineheight8">&nbsp;</Row>
               <Row className="align-items-center my-1 mx-2">
                  <Col className="h6 fw-bold my-0" xs={12}>Machine Type:</Col>
               </Row>
               <Row className="align-items-center my-1 mx-2">
                  <Col xs={12}>
                     <FilterSelect
                        filterName="machineId"
                        options={machines}
                        value={filterHelpers.getValue(filters, "machineId")}
                        onChangeCallback={(e) => onMachineChange(e)}
                        disabled={model.id !== 0}
                     />
                  </Col>
               </Row>

               {/* Identify machine # being recorded */}
               <Row className="lineheight8">&nbsp;</Row>
               <Row className="align-items-center my-1 mx-2">
                  <Col className="h6 fw-bold my-0" xs={12}>Machine #:</Col>
               </Row>
               <Row className="align-items-center my-1 mx-2">
                  <Col xs={12}>
                     <FilterSelect
                        filterName="machineNum"
                        options={machineNums}
                        value={filterHelpers.getValue(filters, "machineNum")}
                        onChangeCallback={(e) => onMachineNumChange(e)}
                        disabled={model.id !== 0}
                     />
                  </Col>
               </Row>

               {/* Identify machine side being recorded */}
               <Row className="lineheight8">&nbsp;</Row>
               <Row className="align-items-center my-1 mx-2">
                  <Col className="h6 fw-bold my-0" xs={12}>Side:</Col>
               </Row>
               <Row className="align-items-center my-1 mx-2">
                  <Col xs={12}>
                     <FilterSelect
                        filterName="side"
                        options={sides}
                        value={filterHelpers.getValue(filters, "side")}
                        onChangeCallback={(e) => onSideChange(e)}
                        disabled={model.id !== 0}
                     />
                  </Col>
               </Row>

               {/* Identify machine spindle being recorded */}
               <Row className="lineheight8">&nbsp;</Row>
               <Row className="align-items-center my-1 mx-2">
                  <Col className="h6 fw-bold my-0" xs={12}>Spindle #:</Col>
               </Row>
               <Row className="align-items-center my-1 mx-2">
                  <Col xs={12}>
                     <FilterSelect
                        filterName="spindle"
                        options={spindles}
                        value={filterHelpers.getValue(filters, "spindle")}
                        onChangeCallback={(e) => onSpindleChange(e)}
                        disabled={model.id !== 0}
                     />
                  </Col>
               </Row>

               {/* Identify cone ID being recorded */}
               <Row className="lineheight8">&nbsp;</Row>
               <Row className="align-items-center my-1 mx-2">
                  <Col className="h6 fw-bold my-0" xs={12}>Cone #:</Col>
               </Row>
               <Row className="align-items-center my-1 mx-2">
                  <Col xs={12}>
                     <Input className="no-spinner h-100"  // This className hides the up/down arrows at end of entry field
                        type="number"
                        name="coneNum"
                        value={cone}
                        onFocus={handleFocus}
                        disabled={model.id !== 0}
                        onChange={(e) => {
                           if (e.target.value.match(/^\d{1,9}$/)) // integer from 1 to 9 digits
                              onConeChange(e.target.value);
                        }}
                     />
                  </Col>
               </Row>

               {model?.id !== 0 ? (
                  <div>
                     {/* Identify pkg weight being recorded */}
                     <Row className="lineheight8">&nbsp;</Row>
                     <Row className="align-items-center my-1 mx-2">
                        <Col className="h6 fw-bold my-0" xs={12}>Package Weight:</Col>
                     </Row>
                     <Row className="align-items-center my-1 mx-2">
                        <Col xs={12}>
                           <Input className="no-spinner h-100"  // This className hides the up/down arrows at end of entry field
                              type="number"
                              name="pkgWgt"
                              value={weight}
                              onFocus={handleFocus}
                              onChange={(e) => {
                                 if (e.target.value.match(/^\d{1,3}(\.\d{0,2})?$/)) // Numeric 1-3 digits and up to 2 decimals allowed
                                    onWeightChange(e.target.value);
                              }}
                           />
                        </Col>
                     </Row>

                     {/* Identify a GOOD vs. BAD item being recorded */}
                     <Row className="lineheight15">&nbsp;</Row>
                     <Row className="align-items-center my-1 mx-2">
                        <Col xs={12}>
                           <Input
                              type="checkbox"
                              value={isGood}
                              className="h6 fw-bold my-0 ms-1 mx-2"
                              onChange={() => onGoodVsBad(!isGood)}
                              checked={isGood}
                              disabled={disabled}
                           />
                           <span className="h6 fw-bold ps-2">Item produced is good</span>
                        </Col>
                     </Row>

                     {/* If item is BAD then a defect type must be selected */}
                     {isGood === false ? (
                        <div>
                           <Row className="align-items-center my-1 mx-2">
                              <Col className="h6 fw-bold my-0" xs={12}>
                                 Defect:
                              </Col>
                           </Row>
                           <Row className="align-items-center my-1 mx-2">
                              <Col xs={12}>
                                 <FilterSelect
                                    filterName="defectId"
                                    options={defects}
                                    value={filterHelpers.getValue(filters, "defectId")}
                                    onChangeCallback={(e) => onDefectChange(e)}
                                    disabled={disabled}
                                 />
                              </Col>
                           </Row>
                        </div>
                     ) : null}

                     {/* Save and Cancel Buttons */}
                     <Row className="lineheight8">&nbsp;</Row>
                     <Row className="align-items-center my-1 mx-2">
                        <Col xs={12}>
                           <SpinnerButton
                              title="Complete"
                              className="shredButton btn-md w-100"
                              onClick={() => updateRecord()}
                              on={saving}
                              disabled={saving}
                           > Complete
                           </SpinnerButton>
                        </Col>
                     </Row>

                     <Row className="lineheight8">&nbsp;</Row>
                     <Row className="align-items-center my-1 mx-2">
                        <Col xs={12}>
                           <SpinnerButton
                              title="Cancel"
                              className="btn btn-md w-100"
                              onClick={() => cancelRecord()}
                              on={saving}
                              disabled={saving}
                           > Cancel
                           </SpinnerButton>
                        </Col>
                     </Row>
                     <br />
                  </div>
               ) : (
                  <div>
                     {/* Save and Cancel Buttons */}
                     <Row className="lineheight8">&nbsp;</Row>
                     <Row className="align-items-center my-1 mx-2">
                        <Col xs={12}>
                           <SpinnerButton
                              title="Save"
                              className="shredButton btn-md w-100"
                              onClick={() => saveRecord()}
                              on={saving}
                              disabled={saving}
                           > Save
                           </SpinnerButton>
                        </Col>
                     </Row>

                     <Row className="lineheight8">&nbsp;</Row>
                     <Row className="align-items-center my-1 mx-2">
                        <Col xs={12}>
                           <SpinnerButton
                              title="Clear"
                              className="btn btn-md w-100"
                              onClick={() => cancelRecord()}
                              on={saving}
                              disabled={saving}
                           > Cancel
                           </SpinnerButton>
                        </Col>
                     </Row>
                     <br />
                  </div>
               )}
            </div>
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}
